import { z } from 'zod';
import { formAnswersSchema } from './form';
import { listQuerySchema } from './organization';
// ───────────────────────────── enums ─────────────────────────────
export const serviceCategorySchema = z.enum([
    'ambulance', 'disaster', 'training', 'volunteer', 'other',
]);
export const serviceRequestStatusSchema = z.enum([
    'submitted', 'acknowledged', 'assigned', 'in_progress',
    'completed', 'rejected', 'cancelled',
]);
export const SERVICE_CATEGORY_LABELS = {
    ambulance: 'Ambulans',
    disaster: 'Bencana',
    training: 'Diklat',
    volunteer: 'Relawan',
    other: 'Lainnya',
};
/**
 * Categories that are URGENT. They sort first in the microsite catalogue and carry the
 * emphasised stripe in the PMI queue.
 *
 * Not a column on `service_type`: whether an ambulance request is urgent is not a
 * per-row editorial choice, and a service line that PMI could mark "not urgent" while
 * it dispatches ambulances would misrepresent itself in the one place a requester is
 * deciding whether to phone instead.
 */
export const URGENT_SERVICE_CATEGORIES = ['ambulance', 'disaster'];
// ───────────────────────────── status machine ─────────────────────────────
/**
 * Legal service request transitions. ONE machine for every category.
 *
 * The handling shape genuinely is shared: somebody accepts it, somebody is put on it,
 * work happens, it ends. Only the words differ, and those are `SERVICE_STATUS_LABELS`.
 *
 * `rejected` stays reachable from `acknowledged` and `assigned`, not only from
 * `submitted`: PMI often discovers it cannot serve a request only after looking into it,
 * and forcing that outcome through `completed` would make the completion rate a lie.
 *
 * `in_progress` cannot be rejected. Once the ambulance has left or the team is on site,
 * the request ends as completed — with a note if it went badly. A rejection after
 * dispatch would erase the fact that PMI turned up.
 */
export const SERVICE_REQUEST_TRANSITIONS = {
    submitted: ['acknowledged', 'rejected', 'cancelled'],
    acknowledged: ['assigned', 'in_progress', 'rejected', 'cancelled'],
    assigned: ['in_progress', 'completed', 'rejected'],
    in_progress: ['completed'],
    completed: [],
    rejected: [],
    cancelled: [],
};
export function canTransitionServiceRequest(from, to) {
    return SERVICE_REQUEST_TRANSITIONS[from].includes(to);
}
/** Statuses in which nothing more will happen. Drives `closedAt` and the backlog count. */
export const TERMINAL_SERVICE_STATUSES = ['completed', 'rejected', 'cancelled'];
/**
 * Statuses the REQUESTER may still withdraw from.
 *
 * Deliberately stops at `assigned`: once a vehicle has been committed or a team briefed,
 * cancelling is a phone call to the people already moving, not a button.
 */
export const REQUESTER_CANCELLABLE_STATUSES = ['submitted', 'acknowledged'];
export function isTerminalServiceStatus(status) {
    return TERMINAL_SERVICE_STATUSES.includes(status);
}
/**
 * What each status is CALLED, per category.
 *
 * A requester must never read `in_progress`. For an ambulance that is "Ambulans
 * berangkat"; for diklat the same state means "Diklat berlangsung". This map is the
 * reason the states could be shared at all — without it, either the vocabulary would be
 * wrong for four categories out of five, or there would be five status machines to keep
 * in step.
 */
const BASE_LABELS = {
    submitted: 'Terkirim',
    acknowledged: 'Diterima petugas',
    assigned: 'Petugas ditugaskan',
    in_progress: 'Sedang diproses',
    completed: 'Selesai',
    rejected: 'Tidak dapat dilayani',
    cancelled: 'Dibatalkan',
};
const CATEGORY_OVERRIDES = {
    ambulance: {
        assigned: 'Ambulans disiapkan',
        in_progress: 'Ambulans berangkat',
    },
    disaster: {
        acknowledged: 'Laporan diterima',
        assigned: 'Tim disiagakan',
        in_progress: 'Penanganan berjalan',
        completed: 'Penanganan selesai',
    },
    training: {
        assigned: 'Jadwal ditetapkan',
        in_progress: 'Diklat berlangsung',
    },
    volunteer: {
        acknowledged: 'Sedang ditinjau',
        assigned: 'Lolos tinjauan',
        in_progress: 'Proses pendaftaran',
        completed: 'Diterima sebagai relawan',
        rejected: 'Belum dapat diterima',
    },
};
export function serviceStatusLabel(category, status) {
    return CATEGORY_OVERRIDES[category]?.[status] ?? BASE_LABELS[status];
}
/** Every label for one category, for a filter dropdown that must list them all. */
export function serviceStatusLabels(category) {
    return { ...BASE_LABELS, ...CATEGORY_OVERRIDES[category] };
}
/** Category-neutral labels, for a queue spanning several service types at once. */
export const SERVICE_STATUS_LABELS = BASE_LABELS;
// ───────────────────────────── input ─────────────────────────────
const phoneSchema = z.string().trim()
    .min(7, 'Nomor telepon terlalu pendek')
    .max(25, 'Nomor telepon terlalu panjang')
    .regex(/^[0-9+()\-\s]+$/, 'Nomor telepon hanya boleh angka dan tanda + - ( )');
export const serviceRegionUpdateSchema = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    contactPhone: phoneSchema.nullish(),
    isActive: z.boolean().optional(),
    sortOrder: z.coerce.number().int().min(0).max(999).optional(),
});
/** One region's availability for a service, with the number a requester should call. */
export const serviceTypeRegionInputSchema = z.object({
    regionId: z.string().uuid(),
    contactPhone: phoneSchema.nullish(),
    isActive: z.boolean().default(true),
});
export const serviceTypeInputSchema = z.object({
    code: z.string().trim().min(2).max(24)
        .regex(/^[A-Z][A-Z0-9-]*$/, 'Kode hanya huruf kapital, angka, dan tanda -'),
    name: z.string().trim().min(2).max(120),
    category: serviceCategorySchema,
    description: z.string().trim().max(1000).nullish(),
    icon: z.string().trim().max(40).nullish(),
    templateId: z.string().uuid().nullish(),
    isActive: z.boolean().default(false),
    sortOrder: z.coerce.number().int().min(0).max(999).default(0),
    regions: z.array(serviceTypeRegionInputSchema).default([]),
});
/** `code` is absent on purpose: it appears in every request number already issued. */
export const serviceTypeUpdateSchema = serviceTypeInputSchema.omit({ code: true }).partial();
/**
 * Creating a request from the microsite.
 *
 * `contactName` and `contactPhone` are in the body rather than taken from the SSO
 * identity, which is the opposite of donor registration. That is deliberate: the person
 * a dispatcher must reach is often not the account holder. A son requests an ambulance
 * from his own phone for his mother's house, and the number to call may be the neighbour
 * who is actually there.
 */
export const serviceRequestInputSchema = z.object({
    regionId: z.string().uuid(),
    contactName: z.string().trim().min(2, 'Nama kontak wajib diisi').max(120),
    contactPhone: phoneSchema,
    answers: formAnswersSchema,
});
export const serviceRequestTransitionSchema = z.object({
    status: serviceRequestStatusSchema,
    note: z.string().trim().max(1000).nullish(),
    /** Vehicle, driver, team, or schedule. Free text — see decision 3 in the spec. */
    assignmentNote: z.string().trim().max(1000).nullish(),
    /** Required by the database when moving to `rejected`. */
    rejectionReason: z.string().trim().max(1000).nullish(),
});
export const serviceRequestSearchSchema = listQuerySchema.extend({
    serviceTypeId: z.string().uuid().optional(),
    regionId: z.string().uuid().optional(),
    status: serviceRequestStatusSchema.optional(),
    /** `open` is the queue's default view: everything not yet terminal. */
    openOnly: z.coerce.boolean().default(false),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
});
export const serviceDashboardQuerySchema = z.object({
    regionId: z.string().uuid().optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
});
